import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Spinner,
  Alert
} from "reactstrap";
import ImportZone from "../../../../pages/DashboardImport/components/ImportZone";
import { useDispatch } from "react-redux";
import Papa from "papaparse";
import employeesImport from "../../../../assets/files-example/baxrx-employees-import.csv";
import Swal from "sweetalert2";
import { postEmployeeByEmployerId } from "../../../../slices/admin/thunk";
import { useCurrentUser } from "../../../../slices/auth/hooks";
import { useParams } from "react-router-dom";
import AddEmployeeTabs from "../../../Tabs/AddEmployeeTabs";

const ImportEmployeesModal = ({
  open,
  setOpen,
  onCreate,
  activeTab,
  toggleTab,
}) => {
  const { id } = useParams();
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();

  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileDownload, setFileDownload] = useState(null);
  const [fileName, setFileName] = useState("");
  const [csvInfo, setCsvInfo] = useState({
    shown: false,
    message: "The CSV should have the following columns: First Name, Last Name, Email, Tier (optional, use 'F' for family tier or 'E' for employee tier - defaults to 'E' if left blank)"
  });

  const toggleModal = () => {
    setOpen(!open);
  };

  useEffect(() => {
    setFileDownload(employeesImport);
    setFileName("baxrx-employees-import.csv");
  }, []);

  const handleDownloadCSV = () => {
    if (fileDownload) {
      const link = document.createElement("a");
      link.href = fileDownload;
      link.download = fileName;
      link.click();
    }
  };

  function handleUpload() {
    const file = selectedFile;
    if (!file) {
      Swal.fire({
        title: "Error!",
        text: "Please select a CSV file.",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }

    const reader = new FileReader();

    reader.onload = async function (event) {
      const csvData = event.target.result;
      const parsedData = Papa.parse(csvData, { header: false }).data;

      // Skip header rows
      const dataWithoutMeta = parsedData.slice(5);

      let errors = [];
      let successful = [];

      dataWithoutMeta.forEach((employeeArray) => {
        // Check if we have at least the required fields (firstName, lastName, email)
        // The fourth column (tier) is optional
        const [firstName, lastName, email, tierValue] = employeeArray;

        // If all required fields are missing, ignore this row
        if (!firstName && !lastName && !email) {
          return null;
        }

        // If any required field is missing, add to errors
        if (!firstName || !lastName || !email) {
          errors.push(employeeArray);
          return null;
        }

        // Check if tier is family (F) - default is employee (E)
        // Only values that represent "F" will be considered family tier
        const isFamily = tierValue
          ? tierValue.toString().toUpperCase().trim() === 'F'
          : false;

        const employeeData = {
          firstName,
          lastName,
          email,
          isFamily,
          tier: isFamily ? "F" : "E", // Default to employee tier if not specified
        };

        successful.push(employeeData);
      });

      if (errors.length > 0) {
        Swal.fire({
          title: "Errors Found!",
          text: `There were errors with the following employees: \n${errors
            .map((e) => e.join(", "))
            .join("\n")}`,
          icon: "warning",
          confirmButtonText: "Ok",
        });
        return;
      }

      const result = await Swal.fire({
        title: "Confirm Upload",
        text: `Are you sure you want to upload ${successful.length} employees?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Confirm",
        cancelButtonText: "Cancel",
      });

      if (result.value) {
        setLoading(true);
        try {
          const res = await dispatch(
            postEmployeeByEmployerId({
              id: id || currentUser.id,
              data: successful,
              multiple: true,
            })
          );
          const response = res.payload;

          const hasFailed = response.failed && response.failed.length > 0;
          const hasSuccessful = response.successful && response.successful.length > 0;

          if (hasFailed && hasSuccessful) {
            Swal.fire({
              title: "Partial Success",
              text: `Successfully imported ${response.successful.length} employees. However, there was an error importing ${response.failed.length} employees.`,
              icon: "warning",
              confirmButtonText: "Ok",
            });
            onCreate();
          } else if (hasFailed) {
            Swal.fire({
              title: "Error!",
              text: `There was an error importing ${response.failed.length} employees. Please check your CSV file format and make sure you haven't already imported these employees.`,
              icon: "error",
              confirmButtonText: "Ok",
            });
          } else if (hasSuccessful) {
            Swal.fire({
              title: "Success!",
              text: `Successfully imported ${response.successful.length} employees.`,
              icon: "success",
              timer: 2000,
              timerProgressBar: true,
              showConfirmButton: false,
            });
            setOpen(false);
            onCreate();
          }
        } catch (err) {
          Swal.fire({
            title: "Error!",
            text: "There was an error importing your employees. Please try again.",
            icon: "error",
            confirmButtonText: "Ok",
          });
          console.error(err);
        }
        setLoading(false);
      }
    };

    reader.readAsText(file);
  }

  return (
    <Modal isOpen={open} toggle={toggleModal} fade={false}>
      <ModalHeader toggle={toggleModal}>Import From CSV</ModalHeader>
      <ModalBody>
        <div className="mb-3">
          <AddEmployeeTabs activeTab={activeTab} toggleTab={toggleTab} />
        </div>

        <Alert color="info" isOpen={csvInfo.shown} toggle={() => setCsvInfo({ ...csvInfo, shown: false })}>
          {csvInfo.message}
        </Alert>

        <div>
          <div className="d-flex align-items-center mb-2">
            <label className="fs-5 fst-italic me-2">Download Base CSV File</label>
            <Button
              color="link"
              className="p-0 d-flex align-items-center"
              onClick={handleDownloadCSV}
            >
              <i className="ri-download-line fs-5 fw-bold"></i>
            </Button>
            <Button
              color="link"
              className="p-0 ms-3 text-muted"
              onClick={() => setCsvInfo({ ...csvInfo, shown: true })}
            >
              <i className="ri-information-line"></i>
            </Button>
          </div>

          <label htmlFor="like-input" className="form-label fs-3 mb-2">
            Import .csv Files
          </label>

          <ImportZone
            onSelectFile={(file) => setSelectedFile(file)}
            acceptedFiles=".csv"
            title="Upload a File (.csv)"
          />

          <Button
            disabled={!selectedFile || loading}
            onClick={handleUpload}
            color="primary"
            className="mt-3 w-100"
            type="submit"
          >
            {loading && (
              <Spinner size="sm" className="me-2" />
            )}
            Add Employees
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ImportEmployeesModal;